import {
  InvalidateQueryFilters,
  keepPreviousData,
  skipToken,
  useQuery,
} from '@tanstack/react-query';
import { getV1CompaniesCompanyIdBillingLinesGroups } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { chunk } from 'lodash-es';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { BillingLineGroup } from '~hooks/useBillingLines/models';
import connection from '~services/connectionModule';
import { fetchAllApiPages } from '~utils/fetchAllPages';

export const BILLING_LINE_GROUPS_QUERY_KEY = 'billingLineGroups';

const GROUP_TOTALS_STALE_TIME_MS = 30_000;

export function makeBillingLineGroupsWithJobsFilters(
  jobIds: string[],
): InvalidateQueryFilters {
  const changedJobIds = new Set(jobIds);

  return {
    predicate: ({ queryKey: [key, , groupJobIds] }) =>
      key === BILLING_LINE_GROUPS_QUERY_KEY &&
      Array.isArray(groupJobIds) &&
      groupJobIds.some(
        (jobId: unknown) => typeof jobId === 'string' && changedJobIds.has(jobId),
      ),
  };
}

export default function useBillingLineGroupsQuery(
  companyId: string,
  jobIds: string[],
  refreshToken: number,
) {
  const canReadGroups = Boolean(companyId) && jobIds.length > 0;
  const { data, isError, isPlaceholderData } = useQuery({
    queryKey: [BILLING_LINE_GROUPS_QUERY_KEY, companyId, jobIds, refreshToken],
    queryFn: canReadGroups
      ? async ({ signal }) => {
          try {
            const lineGroupsPerJobIdsChunk = await Promise.all(
              chunk(jobIds, MAX_API_PAGE_SIZE).map((jobIdsChunk) =>
                fetchAllApiPages(
                  (pageQuery, pageSignal) =>
                    getV1CompaniesCompanyIdBillingLinesGroups({
                      path: { 'company-id': companyId },
                      query: { job_ids: jobIdsChunk, ...pageQuery },
                      signal: pageSignal,
                    }),
                  BillingLineGroup.parse,
                  { signal },
                ),
              ),
            );

            return lineGroupsPerJobIdsChunk.flat();
          } catch (error) {
            connection.handleRequestError(
              error,
              t('error_messages.approve_charges.failed_to_fetch_groups'),
            );
            throw error;
          }
        }
      : skipToken,
    placeholderData: keepPreviousData,
    staleTime: GROUP_TOTALS_STALE_TIME_MS,
  });

  return { isError, isUpdating: isPlaceholderData, lineGroups: data ?? null };
}
